/**
 * ZAXIA Guest - My Requests Hook
 * Fetches and polls guest's service request history
 *
 * Phase P2.2-9: Guest Request History UI
 */

import { useState, useEffect } from 'react';
import { fetchGuestRequests } from '@/api';
import type { ApiGuestRequest } from '@/api';
import { useData } from '@/providers/DataProvider';

interface UseMyRequestsResult {
  requests: ApiGuestRequest[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Fetches guest's service requests filtered by current stayId
 * Polls every 20 seconds for real-time updates
 */
export function useMyRequests(): UseMyRequestsResult {
  const { stay } = useData();
  const [requests, setRequests] = useState<ApiGuestRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadRequests = async () => {
    if (!stay?.stayId) {
      setRequests([]);
      setLoading(false);
      return;
    }

    try {
      setError(null);
      const allRequests = await fetchGuestRequests();

      // Filter by current stay
      const myRequests = allRequests.filter(
        (req) => req.stayId === stay.stayId
      );

      // Sort newest first
      myRequests.sort((a, b) => {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });

      setRequests(myRequests);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load requests');
      console.error('[useMyRequests] Error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadRequests();
  }, [stay?.stayId]);

  // Poll every 20 seconds
  useEffect(() => {
    if (!stay?.stayId) return;

    const interval = setInterval(() => {
      loadRequests();
    }, 20000);

    return () => clearInterval(interval);
  }, [stay?.stayId]);

  return {
    requests,
    loading,
    error,
    refetch: loadRequests,
  };
}
